import * as React from "react";
import { Alert, Button, FormControl, FormGroup } from "react-bootstrap";
import { fetchSurveyDefinition } from "../lib/surveyDefinitionFetch";

/*
 * UTM Wizard — internal staff tool for generating UTM-tagged query strings.
 *
 * Reads the live survey-definition JSON files for the selected audience and
 * builds the utm_source dropdown (and any utm_campaign follow-up) from the
 * actual referral-source options on the form. That way the wizard's choices
 * stay in lock-step with what the form will actually pre-fill.
 */

type Audience = "protege" | "mentor";

interface FollowUp {
  type: "select_single" | "text_simple";
  label: string;
  options?: { [key: string]: string };
}

interface MergedReferral {
  // Map from option key → display label and which form(s) the option came from
  options: {
    [key: string]: { label: string; formsSeen: string[] };
  };
  // Map from main option key → follow-up question (if any)
  followUps: { [optionKey: string]: FollowUp };
}

const AUDIENCE_FORMS: { [a in Audience]: string[] } = {
  protege: ["protege", "protege-spouse", "protege-child"],
  // Corporate mentors don't have a referral-source question — we know where
  // they came from (their company). Only mentor-ctzn is relevant here.
  mentor: ["mentor-ctzn"],
};

const BASE_URLS: { label: string; url: string }[] = [
  { label: "Home Page", url: "https://www.acp-usa.org/" },
  { label: "Programs Page", url: "https://www.acp-usa.org/programs/" },
  {
    label: "Veterans Program Page",
    url: "https://www.acp-usa.org/programs/veteran-mentoring-program/",
  },
  {
    label: "Spouse Program Page",
    url: "https://www.acp-usa.org/programs/active-duty-spouse-program/",
  },
  { label: "Mentor Page", url: "https://www.acp-usa.org/become-a-mentor/" },
  {
    label: "Mentor application (citizens program)",
    url: "https://apply.acp-usa.org/mentor/acpusa",
  },
  {
    label: "Veterans application",
    url: "https://apply.acp-usa.org/protege",
  },
  {
    label: "Spouse application",
    url: "https://apply.acp-usa.org/protege-spouse",
  },
  {
    label: "Surviving Child application",
    url: "https://apply.acp-usa.org/protege-child",
  },
];

function isValidAcpUrl(s: string): boolean {
  // UTM capture only fires on subdomains of acp-usa.org because the cookie
  // is scoped that way (see frontend/src/lib/utmTracking.ts). A link to any
  // other host won't actually carry referral attribution into the app.
  try {
    const u = new URL(s);
    if (u.protocol !== "https:") return false;
    const host = u.hostname.toLowerCase();
    return host === "acp-usa.org" || host.endsWith(".acp-usa.org");
  } catch {
    return false;
  }
}

interface RawQuestion {
  name: string;
  type: string;
  label?: string;
  options?: { [key: string]: string };
  prereq?: { input: string; value: string };
  questions?: RawQuestion[];
  target?: string;
}

interface RawSurvey {
  panes: { questions: RawQuestion[] }[];
}

function findReferralSource(survey: RawSurvey): RawQuestion | undefined {
  for (const pane of survey.panes) {
    for (const q of pane.questions) {
      if (q.target === "referralSource" && q.type === "compound") {
        return q;
      }
    }
  }
  return undefined;
}

function extractFromSurvey(
  survey: RawSurvey,
  formName: string,
): {
  options: { [key: string]: string };
  followUps: { [optionKey: string]: FollowUp };
} {
  const referral = findReferralSource(survey);
  if (!referral || !referral.questions) {
    return { options: {}, followUps: {} };
  }

  const main = referral.questions.find((q) => q.name === "Referral.Main");
  const options = main?.options ?? {};

  const followUps: { [optionKey: string]: FollowUp } = {};
  for (const sub of referral.questions) {
    if (sub.name === "Referral.Main") continue;
    if (!sub.prereq || sub.prereq.input !== "Referral.Main") continue;
    if (sub.type !== "select_single" && sub.type !== "text_simple") continue;
    followUps[sub.prereq.value] = {
      type: sub.type as "select_single" | "text_simple",
      label: sub.label ?? "Follow-up",
      options: sub.options,
    };
  }

  // Suppress unused-var lint by referencing formName (kept in signature for
  // future use if we want to surface form-of-origin in callers).
  void formName;

  return { options, followUps };
}

async function loadAudience(audience: Audience): Promise<MergedReferral> {
  const forms = AUDIENCE_FORMS[audience];
  const merged: MergedReferral = { options: {}, followUps: {} };

  for (const form of forms) {
    const res = await fetchSurveyDefinition<RawSurvey>(
      `/survey-definitions/${form}.json`,
    );
    const { options, followUps } = extractFromSurvey(res.data, form);
    for (const [key, label] of Object.entries(options)) {
      const existing = merged.options[key];
      if (existing) {
        existing.formsSeen.push(form);
      } else {
        merged.options[key] = { label, formsSeen: [form] };
      }
    }
    for (const [key, followUp] of Object.entries(followUps)) {
      // First form wins; mentor pair are identical and protege/protege-spouse
      // follow-ups are identical too. If they ever diverge, this is a place
      // to revisit.
      if (!merged.followUps[key]) {
        merged.followUps[key] = followUp;
      }
    }
  }

  return merged;
}

function buildQueryString(
  source: string,
  campaign: string,
  medium: string,
  content: string,
): string {
  if (!source) return "";
  const parts: string[] = [`utm_source=${encodeURIComponent(source)}`];
  if (campaign) parts.push(`utm_campaign=${encodeURIComponent(campaign)}`);
  if (medium) parts.push(`utm_medium=${encodeURIComponent(medium)}`);
  if (content) parts.push(`utm_content=${encodeURIComponent(content)}`);
  return `?${parts.join("&")}`;
}

const UtmWizard: React.FC = () => {
  const [audience, setAudience] = React.useState<Audience>("protege");
  const [merged, setMerged] = React.useState<MergedReferral | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [utmSource, setUtmSource] = React.useState<string>("");
  const [utmCampaign, setUtmCampaign] = React.useState<string>("");
  const [utmMedium, setUtmMedium] = React.useState<string>("affiliate");
  const [utmContent, setUtmContent] = React.useState<string>("");
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);
  const [copied, setCopied] = React.useState<boolean>(false);
  // Default to "Programs Page" — index 1 in BASE_URLS.
  const [baseUrlChoice, setBaseUrlChoice] = React.useState<string>("1");
  const [customBaseUrl, setCustomBaseUrl] = React.useState<string>("");

  React.useEffect(() => {
    let cancelled = false;
    setMerged(null);
    setLoadError(null);
    setUtmSource("");
    setUtmCampaign("");
    loadAudience(audience)
      .then((m) => {
        if (!cancelled) setMerged(m);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load survey definitions:", err);
          setLoadError(String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [audience]);

  // When the source changes, clear any existing campaign value
  React.useEffect(() => {
    setUtmCampaign("");
  }, [utmSource]);

  const followUp = merged && utmSource ? merged.followUps[utmSource] : undefined;
  const sourceMeta = merged && utmSource ? merged.options[utmSource] : undefined;

  const queryString = buildQueryString(
    utmSource,
    utmCampaign,
    utmMedium,
    utmContent,
  );

  const baseUrl =
    baseUrlChoice === "other"
      ? customBaseUrl
      : BASE_URLS[Number(baseUrlChoice)].url;
  const fullUrl = baseUrl + queryString;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  // Sort options alphabetically by label, with the form-coverage in mind.
  const sortedOptionEntries = merged
    ? Object.entries(merged.options).sort(([, a], [, b]) =>
        a.label.localeCompare(b.label),
      )
    : [];

  const allForms = AUDIENCE_FORMS[audience];

  return (
    <div style={{ maxWidth: 720, margin: "1em auto", padding: "0 1em" }}>
      <h2>
        <span role="img" aria-label="wizard">
          🧙
        </span>{" "}
        UTM Wizard
      </h2>
      <p className="text-muted">
        Pick an audience and a referral source, then choose (or paste) the
        page you're sharing. The full UTM-tagged URL appears at the bottom,
        ready to copy.
      </p>

      <FormGroup>
        <label>
          <strong>Audience</strong>
        </label>
        <div>
          <label style={{ marginRight: "1.5em", fontWeight: "normal" }}>
            <input
              type="radio"
              name="audience"
              value="protege"
              checked={audience === "protege"}
              onChange={() => setAudience("protege")}
            />{" "}
            Protégé
          </label>
          <label style={{ fontWeight: "normal" }}>
            <input
              type="radio"
              name="audience"
              value="mentor"
              checked={audience === "mentor"}
              onChange={() => setAudience("mentor")}
            />{" "}
            Mentor
          </label>
        </div>
      </FormGroup>

      {loadError && (
        <Alert bsStyle="danger">
          Could not load survey definitions: {loadError}
        </Alert>
      )}

      {!merged && !loadError && <p>Loading…</p>}

      {merged && (
        <>
          <FormGroup>
            <label>
              <strong>utm_source</strong>
            </label>
            <FormControl
              componentClass="select"
              value={utmSource}
              onChange={(e) =>
                setUtmSource((e.target as HTMLSelectElement).value)
              }
            >
              <option value="">— pick a source —</option>
              {sortedOptionEntries.map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label} ({key})
                </option>
              ))}
            </FormControl>
            {sourceMeta && sourceMeta.formsSeen.length < allForms.length && (
              <p
                className="text-muted"
                style={{ marginTop: "0.5em", fontSize: "0.9em" }}
              >
                Note: this option only exists on{" "}
                <code>{sourceMeta.formsSeen.join(", ")}</code>. The URL will
                still record the UTM, but the form field will only pre-fill
                on those.
              </p>
            )}
          </FormGroup>

          {followUp && (
            <FormGroup>
              <label>
                <strong>utm_campaign</strong> — {followUp.label}
              </label>
              {followUp.type === "select_single" ? (
                <FormControl
                  componentClass="select"
                  value={utmCampaign}
                  onChange={(e) =>
                    setUtmCampaign((e.target as HTMLSelectElement).value)
                  }
                >
                  <option value="">— pick one —</option>
                  {Object.entries(followUp.options ?? {})
                    .sort(([, a], [, b]) => a.localeCompare(b))
                    .map(([key, label]) => (
                      <option key={key} value={key}>
                        {label} ({key})
                      </option>
                    ))}
                </FormControl>
              ) : (
                <FormControl
                  type="text"
                  value={utmCampaign}
                  onChange={(e) =>
                    setUtmCampaign((e.target as HTMLInputElement).value)
                  }
                  placeholder="Free-form value (e.g., installation or college name)"
                />
              )}
            </FormGroup>
          )}

          <FormGroup>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowAdvanced(!showAdvanced);
              }}
            >
              {showAdvanced ? "Hide" : "Show"} advanced
            </a>
            {showAdvanced && (
              <div style={{ marginTop: "0.5em" }}>
                <label>
                  <strong>utm_medium</strong>
                </label>
                <FormControl
                  type="text"
                  value={utmMedium}
                  onChange={(e) =>
                    setUtmMedium((e.target as HTMLInputElement).value)
                  }
                  placeholder="affiliate"
                />
                <label style={{ marginTop: "0.5em" }}>
                  <strong>utm_content</strong>
                </label>
                <p
                  className="text-muted"
                  style={{ marginBottom: "0.25em", fontSize: "0.9em" }}
                >
                  Which of a partner's links this is (e.g. <code>portal</code>,{" "}
                  <code>newsletter</code>). Recorded for reporting, but never
                  shown in the applicant's referral source — use this instead of
                  utm_campaign when you want the referral source to stay plain.
                </p>
                <FormControl
                  type="text"
                  value={utmContent}
                  onChange={(e) =>
                    setUtmContent((e.target as HTMLInputElement).value)
                  }
                  placeholder="portal"
                />
              </div>
            )}
          </FormGroup>

          <hr />

          <FormGroup>
            <label>
              <strong>Final URL</strong>
            </label>
            <p
              className="text-muted"
              style={{ marginBottom: "0.5em", fontSize: "0.9em" }}
            >
              Pick the page you're sharing. The{" "}
              <span style={{ color: "#888" }}>grey</span> part is the page
              URL itself; the{" "}
              <span style={{ color: "#1f6feb", fontWeight: 600 }}>blue</span>{" "}
              part is what the wizard adds. Tracking only works on{" "}
              <code>acp-usa.org</code> subdomains — other URLs will be flagged
              and the copy button disabled.
            </p>
            <FormControl
              componentClass="select"
              value={baseUrlChoice}
              onChange={(e) =>
                setBaseUrlChoice((e.target as HTMLSelectElement).value)
              }
              style={{ marginBottom: "0.5em" }}
            >
              {BASE_URLS.map((b, i) => (
                <option key={i} value={String(i)}>
                  {b.label} — {b.url}
                </option>
              ))}
              <option value="other">Other…</option>
            </FormControl>
            {baseUrlChoice === "other" && (
              <FormControl
                type="text"
                value={customBaseUrl}
                onChange={(e) =>
                  setCustomBaseUrl((e.target as HTMLInputElement).value)
                }
                placeholder="https://www.acp-usa.org/some/page"
                style={{ marginBottom: "0.5em" }}
              />
            )}
            <div
              style={{
                padding: "0.5em 0.75em",
                background: "#fafafa",
                border: "1px solid #ddd",
                borderRadius: 3,
                fontFamily: "monospace",
                wordBreak: "break-all",
                marginBottom: "0.5em",
              }}
            >
              <span style={{ color: "#888" }}>
                {baseUrl ||
                  (baseUrlChoice === "other"
                    ? "https://www.acp-usa.org/some/page"
                    : "")}
              </span>
              <span style={{ color: "#1f6feb", fontWeight: 600 }}>
                {queryString || "?utm_source=…"}
              </span>
            </div>
            {baseUrl !== "" && !isValidAcpUrl(baseUrl) && (
              <p
                className="text-danger"
                style={{ marginBottom: "0.5em", fontSize: "0.9em" }}
              >
                That URL isn't on an <code>acp-usa.org</code> subdomain (or
                isn't <code>https</code>), so UTM tracking won't fire. Use a
                different base URL.
              </p>
            )}
            <Button
              bsStyle={copied ? "success" : "primary"}
              onClick={onCopy}
              disabled={!queryString || !isValidAcpUrl(fullUrl)}
            >
              {copied ? "Copied!" : "Copy URL"}
            </Button>
          </FormGroup>
        </>
      )}
    </div>
  );
};

export default UtmWizard;
